import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';

import {
  decodeHtmlEntities,
  containsHtml,
  sanitizeHtml,
  stripHtmlToText,
} from './messageHtmlUtils';

const safeUrl = raw => {
  try {
    if (!raw || /^data:/i.test(raw)) return null;
    const base =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : 'http://localhost';
    return new URL(raw, base);
  } catch {
    return null;
  }
};

const VALID_DIMENSION =
  /^(?:auto|inherit|initial|unset|fit-content|min-content|max-content|calc\(.+\))|^(?:\d+(?:\.\d+)?)(?:px|%|em|rem|vh|vw)?$/i;

const setDimension = (inlineToken, key, value) => {
  if (!value) return;

  let cssValue = value.trim();
  if (/^\d+(\.\d+)?$/.test(cssValue)) {
    cssValue += 'px';
  }

  if (!VALID_DIMENSION.test(cssValue)) return;
  const existingStyle = inlineToken.attrGet('style') || '';
  const styles = existingStyle
    .split(';')
    .map(s => s.trim())
    .filter(s => {
      return s && !new RegExp(`^${key}\\s*:`, 'i').test(s);
    });
  styles.push(`${key}: ${cssValue};`);
  inlineToken.attrSet('style', styles.join(' '));
};

const setImageDimensions = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = safeUrl(imgSrc);
  if (!url) return;

  const h = url.searchParams.get('cw_image_height');
  const w = url.searchParams.get('cw_image_width');
  if (h) setDimension(inlineToken, 'height', h);
  if (w) setDimension(inlineToken, 'width', w);
};

const processInlineToken = blockToken => {
  if (!blockToken.children) return;

  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') setImageDimensions(inlineToken);
  });
};

const imgResizeManager = md => {
  md.core.ruler.after('inline', 'add-image-dimensions', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') processInlineToken(blockToken);
    });
  });
};

const createMarkdownInstance = (linkify = true) =>
  MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

class MessageFormatter {
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.md = createMarkdownInstance(linkify);
  }

  replaceTwitterTags(text) {
    if (!this.isATweet || this.isAPrivateNote) return text;
    return text
      .replace(TWITTER_USERNAME_REGEX, TWITTER_USERNAME_REPLACEMENT)
      .replace(TWITTER_HASH_REGEX, TWITTER_HASH_REPLACEMENT);
  }

  renderMessage({ safe = false } = {}) {
    if (!this.message) return '';
    let updated = safe ? decodeHtmlEntities(this.message) : this.message;

    updated = this.replaceTwitterTags(updated);
    if (containsHtml(updated)) {
      return sanitizeHtml(updated);
    }

    return this.md.render(updated);
  }

  get formattedMessage() {
    return this.renderMessage();
  }

  get safeFormattedMessage() {
    return this.renderMessage({ safe: true });
  }

  get plainText() {
    return stripHtmlToText(this.message);
  }
}

export default MessageFormatter;
